import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import {
  compileNumberFormat,
  eatDayOf,
  formatHumanNumber,
  parseHumanNumber,
  type HumanNumberErrorCode,
} from '@wodi/domain';
import type { NumberFormatView, SequenceKind } from '@wodi/shared';
import { Button, LoadingBlock, Notice, PageHeader, TextField } from '@wodi/ui';
import { useState } from 'react';

import { ApiError } from '../api/client.ts';
import { can, sessionQuery } from '../api/session.ts';
import { numberFormatsQuery, saveNumberFormat } from '../api/settings.ts';
import { useOnline } from '../hooks/use-online.ts';
import { Section } from '../patients/record-sections.tsx';
import styles from '../patients/patients.module.css';
import { toasts } from '../toasts.ts';
import { SettingsPage } from './settings-nav.tsx';

const KIND_LABELS: Record<SequenceKind, string> = {
  patient_number: 'Patient numbers',
  visit_number: 'Visit numbers',
  chart_number: 'Chart numbers',
};

/** The interface's words for each format problem. */
export const FORMAT_ERROR_MESSAGES: Record<HumanNumberErrorCode, string> = {
  'human_number.empty_format': 'Enter a format.',
  'human_number.malformed_placeholder': 'A { has no matching }, or a } stands on its own.',
  'human_number.unknown_placeholder':
    'Use only {facility}, {seq}, {seq:6}, {yy}, {yyyy}, {mm} and {dd}.',
  'human_number.invalid_width': 'The number of digits in {seq:n} must be 1 to 20.',
  'human_number.missing_sequence': 'Include {seq}, the running number.',
  'human_number.repeated_sequence': 'Include {seq} only once.',
  'human_number.ambiguous_format':
    'Put a separator, like a hyphen, after {facility} or {seq} so numbers can be read back.',
  'human_number.invalid_sequence': 'The next number is not valid.',
  'human_number.missing_facility_code':
    'This facility has no number code yet, so {facility} can’t be used.',
  'human_number.invalid_facility_code': 'The facility code can only have letters and digits.',
  'human_number.missing_day': 'A date is needed for this format.',
  'human_number.invalid_day': 'That date does not exist.',
  'human_number.does_not_match': 'That number does not match the format.',
};

/**
 * Renders the next number in a draft format, using the facility code read back out of
 * the saved example so the preview matches what the server will issue.
 */
export function previewNumber(
  view: NumberFormatView,
  draft: string,
  now: Date,
): { ok: true; value: string } | { ok: false; message: string } {
  const compiled = compileNumberFormat(draft.trim());
  if (!compiled.ok) return { ok: false, message: FORMAT_ERROR_MESSAGES[compiled.code] };
  const saved = compileNumberFormat(view.format);
  const parsed = saved.ok ? parseHumanNumber(saved.value, view.example) : null;
  const facilityCode = parsed?.ok === true ? parsed.value.facilityCode : undefined;
  const rendered = formatHumanNumber(compiled.value, {
    sequence: BigInt(view.nextValue),
    day: eatDayOf(now),
    ...(facilityCode === undefined ? {} : { facilityCode }),
  });
  return rendered.ok
    ? { ok: true, value: rendered.value }
    : { ok: false, message: FORMAT_ERROR_MESSAGES[rendered.code] };
}

function FormatEditor({ view }: { view: NumberFormatView }) {
  const queryClient = useQueryClient();
  const online = useOnline();
  const [draft, setDraft] = useState(view.format);
  const preview = previewNumber(view, draft, new Date());
  const changed = draft.trim() !== view.format;
  const save = useMutation({
    mutationFn: () =>
      saveNumberFormat({ facilityId: view.facilityId, kind: view.kind, format: draft.trim() }),
    onSuccess: async (rows) => {
      queryClient.setQueryData(numberFormatsQuery.queryKey, rows);
      toasts.show({ title: 'Number format saved', description: KIND_LABELS[view.kind] });
    },
  });
  return (
    <form
      className={styles.inlineForm}
      onSubmit={(event) => {
        event.preventDefault();
        if (preview.ok && changed) save.mutate();
      }}
    >
      <TextField
        label={KIND_LABELS[view.kind]}
        value={draft}
        onChange={setDraft}
        {...(preview.ok
          ? {
              description: `The next number will be ${preview.value}. Numbers already issued don't change.`,
            }
          : { errorMessage: preview.message })}
      />
      <p className={styles.meta}>
        Current: <span className="wodi-id">{view.example}</span>
      </p>
      {save.error === null ? null : (
        <Notice tone="error">
          {save.error instanceof ApiError
            ? save.error.message
            : "The format wasn't saved. Try again."}
        </Notice>
      )}
      <div className={styles.actions}>
        <Button
          type="submit"
          variant="primary"
          isDisabled={!online || !changed || !preview.ok}
          isPending={save.isPending}
          pendingLabel="Saving…"
        >
          {`Save ${KIND_LABELS[view.kind].toLowerCase()} format`}
        </Button>
      </div>
    </form>
  );
}

/**
 * Settings > Patient and visit numbers (plan §6.6; decision 5, still open): the format
 * each facility issues numbers in, with a live example from the same domain helper the
 * server uses.
 */
export function NumberFormatsPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const allowed = session !== null && can(session, 'admin.facilities.manage');
  const formats = useQuery({ ...numberFormatsQuery, enabled: allowed });

  if (!allowed) {
    return (
      <>
        <PageHeader title="Patient and visit numbers" />
        <Notice tone="info">
          You don't have permission to change facility settings. Ask an administrator if you need
          it.
        </Notice>
      </>
    );
  }

  const facilityName = (id: string) =>
    session.facilities.find((f) => f.id === id)?.name ?? 'Another facility';
  const byFacility = new Map<string, NumberFormatView[]>();
  for (const view of formats.data ?? []) {
    byFacility.set(view.facilityId, [...(byFacility.get(view.facilityId) ?? []), view]);
  }

  return (
    <SettingsPage title="Patient and visit numbers">
      <p>
        Placeholders: {'{facility}'} is the facility's code, {'{seq:6}'} the running number padded
        to 6 digits, {'{yy}'} or {'{yyyy}'} the year. The format is provisional until the numbering
        rule is agreed.
      </p>
      {formats.isPending ? <LoadingBlock label="Loading number formats" rows={3} /> : null}
      {formats.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void formats.refetch()}>
              Try again
            </Button>
          }
        >
          {formats.error instanceof ApiError
            ? formats.error.message
            : "Number formats couldn't be loaded. Try again."}
        </Notice>
      ) : null}
      {formats.isSuccess && byFacility.size === 0 ? (
        <Notice tone="info">No facilities have number formats yet.</Notice>
      ) : null}
      {[...byFacility.entries()].map(([facilityId, views]) => (
        <Section key={facilityId} title={facilityName(facilityId)}>
          {views.map((view) => (
            <FormatEditor key={`${view.facilityId}-${view.kind}`} view={view} />
          ))}
        </Section>
      ))}
    </SettingsPage>
  );
}
